import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
    BaseChart,
    Button,
    Checkbox,
    LoadingSpinner,
    mapPlaylistFromVideoSearch,
    pitchSubTypeColor,
    pitchTypeDisplayName,
    pitchTypeOrSubType,
} from 'yankees-react-utils';
import { useTranslation } from 'react-i18next';
import { useOktaAuth } from '@okta/okta-react';
import { Video } from '../../../../data';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';

const StyledChartsWrapper = styled.div`
    border: 1px solid rgb(188, 200, 209);
    background-color: white;

    .highcharts-title {
        font-size: 18px;
        font-weight: bold;
        text-align: center;
        font-family: "Aleo", serif;
        letter-spacing: .2px;
    }

    .btn-link:focus {
        box-shadow: none;
    }
`;

const StyledCheckboxWrapper = styled.div`
    .nyy-checkbox-radio-container {
        display: flex;
    }
    & input[type="checkbox"]:checked + label::before {
        background-color: ${(props) => props.$color} !important;
        border-color: ${(props) => props.$color} !important;
    }
`;

const getOptions = (translate, pitchTypes, pitches, pitchVideoFn, xAxisTitle, yAxisTitle, xKey, yKey, xMin = null, xMax = null, yMin = null, yMax = null, isBreak = false) => ({
    chart: {
        type: 'scatter',
        height: 500,
        width: 500,
    },
    title: {
        text: `${xAxisTitle} ${translate('baseball.abbr-versus')} ${yAxisTitle}`,
    },
    xAxis: {
        min: xMin,
        max: xMax,
        title: {
            text: xAxisTitle,
            enabled: true,
        },
        plotLines: isBreak ? [{
            value: 0,
            color: '#e6e6e6',
        }] : [],
    },
    yAxis: {
        min: yMin,
        max: yMax,
        title: {
            text: yAxisTitle,
            enabled: true,
        },
    },
    legend: {
        enabled: false,
    },
    series: pitchTypes.map((pt) => ({
        name: pitchTypeDisplayName(pt, undefined, undefined, translate),
        color: pitchSubTypeColor(pt),
        marker: {
            symbol: 'circle',
        },
        data: pitches.filter((p) => p.pitchtype === pt.pitchtype && p.fxsubtype === pt.fxsubtype).map((p) => ({
            x: p[xKey],
            y: p[yKey],
            gameid: p.gameid,
            eventseq: p.eventseq,
            pitchseq: p.pitchseq,
        })),
    })),
    tooltip: {
        formatter() {
            return `<b>${this.point.series.name}</b><br />${xAxisTitle}: ${this.x.toFixed(1)}<br />${yAxisTitle}:${this.y.toFixed(1)}`;
        },
        hideDelay: 0,
    },
    plotOptions: {
        scatter: {
            stickyTracking: false,
        },
        series: {
            turboThreshold: 0,
            point: {
                events: {
                    click() {
                        pitchVideoFn(this.options.gameid, this.options.eventseq, this.options.pitchseq);
                    },
                },
            },
        },
    },
});

const PitchCharts = ({ className, isLoading, pitches, pitchTypes }) => {
    const { t: translate } = useTranslation();
    const { authState: { accessToken } } = useOktaAuth();

    const modalContainer = useRef();

    const [showVideoPlayerModal, setShowVideoPlayerModal] = useState(false);
    const [playlist, setPlaylist] = useState(null);
    const [selectedPitchTypes, setSelectedPitchTypes] = useState(pitchTypes);

    useEffect(() => {
        setSelectedPitchTypes([...pitchTypes]);
    }, [pitchTypes]);

    const pitchVideoFn = async (gameid, eventseq, pitchseq) => {
        setShowVideoPlayerModal(true);
        const response = await Video.search(accessToken, { gameid, eventseq, pitchseq });
        setPlaylist(mapPlaylistFromVideoSearch(response));
    };

    const onPitchTypeClick = (pitchType) => {
        if (selectedPitchTypes.some((spt) => spt.pitchtype === pitchType.pitchtype && spt.fxsubtype === pitchType.fxsubtype)) {
            setSelectedPitchTypes(selectedPitchTypes.filter((spt) => spt.pitchtype !== pitchType.pitchtype || spt.fxsubtype !== pitchType.fxsubtype));
        } else {
            setSelectedPitchTypes([...selectedPitchTypes, pitchType]);
        }
    };

    if (isLoading) {
        return (
            <div className={`${className} py-5 text-center`}>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <StyledChartsWrapper className={`${className} rounded pt-3`} ref={modalContainer}>
            {pitches.length
                ? (
                    <>
                        <div className="d-flex flex-wrap justify-content-around">
                            <div>
                                <BaseChart
                                    options={getOptions(
                                        translate,
                                        selectedPitchTypes,
                                        pitches,
                                        pitchVideoFn,
                                        translate('data.release-x'),
                                        translate('data.release-z'),
                                        'initposx',
                                        'initposz',
                                    )}
                                />
                            </div>
                            <div>
                                <BaseChart
                                    options={getOptions(
                                        translate,
                                        selectedPitchTypes,
                                        pitches,
                                        pitchVideoFn,
                                        translate('baseball.break-x'),
                                        translate('baseball.break-z'),
                                        'breakx54',
                                        'breakz54',
                                        -30,
                                        30,
                                        -30,
                                        30,
                                        true,
                                    )}
                                />
                            </div>
                            <div>
                                <BaseChart
                                    options={getOptions(
                                        translate,
                                        selectedPitchTypes,
                                        pitches,
                                        pitchVideoFn,
                                        translate('baseball.break-x'),
                                        translate('data.velocity'),
                                        'breakx54',
                                        'velocity',
                                        -30,
                                        30,
                                        65,
                                        105,
                                        true,
                                    )}
                                />
                            </div>
                        </div>
                        <div className="d-flex justify-content-center align-items-center pb-4 mt-3">
                            {pitchTypes.map((pt) => (
                                <div className="d-flex align-items-center mx-2" key={`${pt.pitchtype}-${pt.fxsubtype}`}>
                                    <StyledCheckboxWrapper $color={pitchSubTypeColor(pt)}>
                                        <Checkbox
                                            id={pitchTypeOrSubType(pt.fxsubtype || pt.pitchtype)}
                                            checked={selectedPitchTypes.some((spt) => spt.pitchtype === pt.pitchtype && spt.fxsubtype === pt.fxsubtype)}
                                            onClick={() => onPitchTypeClick(pt)}
                                        />
                                    </StyledCheckboxWrapper>
                                    <Button variant="link" onClick={() => setSelectedPitchTypes([pt])} className="ps-0">
                                        {pitchTypeOrSubType(pt.fxsubtype || pt.pitchtype)}
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </>
                )
                : (
                    <div className="py-5 text-center fs-6">
                        {translate('pitcher-overview.no-data')}
                    </div>
                )}
            <VideoPlayerModal
                show={showVideoPlayerModal}
                onHide={() => { setShowVideoPlayerModal(false); setPlaylist(null); }}
                playlist={playlist}
                container={modalContainer}
            />
        </StyledChartsWrapper>
    );
};

export default PitchCharts;
